import React from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIInput from '@/components/shared/UIInput';
import UISelect from '@/components/shared/UISelect';
import UICheckbox from '@/components/shared/UICheckbox';
import UIButton from '@/components/shared/UIButton';
import UISpinner from '@/components/shared/UISpinner';
import { useProfile } from '@/hooks/useProfile';
import './BillingInformationSection.scss';

export interface BillingInformationSectionProps {
  className?: string;
}

const baseClass = 'BillingInformationSection';

const accountTypeOptions = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'business', label: 'Business' },
];

const BillingInformationSection: React.FC<BillingInformationSectionProps> = ({ className = '' }) => {
  const {
    data,
    initialData,
    loading,
    handleChange,
    handleSave,
    handleClear,
    getAgreementValue,
    setAgreementValue,
    isFormValid,
  } = useProfile('billing');

  const handleAgreementChange = (checked: boolean) => {
    setAgreementValue('billing_authorization', checked);
  };

  const isValid = isFormValid();

  const hasChanges =
    data.account_holder_first_name !== initialData.account_holder_first_name ||
    data.account_holder_last_name !== initialData.account_holder_last_name ||
    data.bank_name !== initialData.bank_name ||
    data.bank_branch_address !== initialData.bank_branch_address ||
    data.transit_number !== initialData.transit_number ||
    data.institution_number !== initialData.institution_number ||
    data.account_number !== initialData.account_number ||
    data.account_type !== initialData.account_type ||
    data.swift_bic_code !== initialData.swift_bic_code ||
    data.billing_authorization !== initialData.billing_authorization;

  if (loading) {
    return (
      <div className={`${baseClass}__loading`} style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <UISpinner />
      </div>
    );
  }

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="large"
      labelWidth="179px"
      formGap="15px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Banking information"
          description="Update your banking information for direct deposits and payments."
        />
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Account holder name</UIFormLabel>
            <UIFormLabel>Bank name</UIFormLabel>
            <UIFormLabel>Bank branch address</UIFormLabel>
            <UIFormLabel>Transit number (branch)</UIFormLabel>
            <UIFormLabel>Institution number</UIFormLabel>
            <UIFormLabel>Account number</UIFormLabel>
            <UIFormLabel>Type of account</UIFormLabel>
            <UIFormLabel>Swift/BIC code (if relevant)</UIFormLabel>
            <UIFormLabel>Date account was added</UIFormLabel>
            <UIFormLabel>&nbsp;</UIFormLabel>
          </UIFormLabels>

          <UIFormFields>
            <div className={`${baseClass}__name-row`}>
              <UIInput
                placeholder="First name"
                value={data.account_holder_first_name || ''}
                onChange={(e) => handleChange('account_holder_first_name', e.target.value)}
              />
              <UIInput
                placeholder="Last name"
                value={data.account_holder_last_name || ''}
                onChange={(e) => handleChange('account_holder_last_name', e.target.value)}
              />
            </div>
            <UIInput
              placeholder="Bank name"
              fullWidth
              value={data.bank_name || ''}
              onChange={(e) => handleChange('bank_name', e.target.value)}
            />
            <UIInput
              placeholder="Address"
              fullWidth
              value={data.bank_branch_address || ''}
              onChange={(e) => handleChange('bank_branch_address', e.target.value)}
            />
            <UIInput
              placeholder="Transit number"
              fullWidth
              value={data.transit_number || ''}
              onChange={(e) => handleChange('transit_number', e.target.value)}
            />
            <UIInput
              placeholder="Institution number"
              fullWidth
              value={data.institution_number || ''}
              onChange={(e) => handleChange('institution_number', e.target.value)}
            />
            <UIInput
              placeholder="Account number"
              fullWidth
              value={data.account_number || ''}
              onChange={(e) => handleChange('account_number', e.target.value)}
            />
            <UISelect
              options={accountTypeOptions}
              placeholder="Select type"
              fullWidth
              value={data.account_type || ''}
              onChange={(e) => handleChange('account_type', e.target.value)}
            />
            <UIInput
              placeholder="Swift/BIC code"
              fullWidth
              value={data.swift_bic_code || ''}
              onChange={(e) => handleChange('swift_bic_code', e.target.value)}
            />
            <div className={`${baseClass}__date-field`}>
              <span>-</span>
            </div>

            <div className={`${baseClass}__agreement-checkbox`}>
              <UICheckbox
                size="medium"
                checked={getAgreementValue('billing_authorization')}
                onChange={(e) => handleAgreementChange(e.target.checked)}
                label="I authorize PropSavvy to store my billing information per PCI-DSS guidelines."
              />
            </div>
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton
            variant="primary"
            onClick={handleSave}
            disabled={!isValid || !hasChanges}
          >
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleClear}>
            Clear
          </UIButton>
        </div>
      </UISectionFooter>
    </UISectionWrapper>
  );
};

export default BillingInformationSection;